import { Injectable, signal, computed } from '@angular/core';

export type HelpTopicId =
  | 'cerimoniais_lista'
  | 'atividades_biblioteca'
  | 'adicionar_lote'
  | 'adicionar_atividade'
  | 'momento_vs_atividade'
  | 'mensagem_telao'
  | 'painel_controle_guia';

export interface HelpTopicSection {
  title?: string;
  description: string;
  bulletPoints?: string[];
  callout?: {
    type: 'tip' | 'info' | 'concept';
    text: string;
  };
}

export interface HelpTopic {
  id: HelpTopicId;
  badge: string;
  badgeType?: 'primary' | 'success' | 'warning' | 'info';
  title: string;
  subtitle: string;
  sections: HelpTopicSection[];
  practicalExample?: {
    title: string;
    content: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class HelpModalService {
  private activeTopicState = signal<HelpTopic | null>(null);

  // Sinal público somente leitura
  readonly activeTopic = this.activeTopicState.asReadonly();
  readonly isOpen = computed(() => !!this.activeTopicState());

  // Catálogo completo com os 7 tópicos didáticos de negócio
  private readonly topicsCatalog: Record<HelpTopicId, HelpTopic> = {
    cerimoniais_lista: {
      id: 'cerimoniais_lista',
      badge: 'Módulo Cerimoniais',
      badgeType: 'primary',
      title: 'Gestão e Governança de Cerimoniais',
      subtitle: 'Como criar, organizar e gerenciar os cronogramas de cultos e eventos litúrgicos.',
      sections: [
        {
          title: 'Criação e Planejamento',
          description: 'Permite criar cronogramas completos informando título, data e horário de início previsto.',
          bulletPoints: [
            'Defina a data e o horário inicial para o cálculo sequencial automático.',
            'Estruture seus eventos semanais ou celebrações especiais com antecedência.'
          ]
        },
        {
          title: 'Duplicação Inteligente',
          description: 'Ao duplicar um evento existente, toda a estrutura de blocos, durações e ordem é preservada, limpando automaticamente os responsáveis designados.',
          callout: {
            type: 'tip',
            text: 'Excelente para cultos com ordem litúrgica recorrente: basta duplicar o cerimonial anterior e atualizar apenas os nomes dos participantes!'
          }
        },
        {
          title: 'Filtros e Busca Instantânea',
          description: 'Navegue mês a mês pelo calendário integrado ou localize cerimoniais por título ou data através da barra de pesquisa em tempo real.'
        }
      ]
    },

    atividades_biblioteca: {
      id: 'atividades_biblioteca',
      badge: 'Biblioteca Reutilizável',
      badgeType: 'info',
      title: 'Repositório de Atividades Padrão',
      subtitle: 'Padronize a duração e nomenclatura das atividades recorrentes da sua igreja.',
      sections: [
        {
          title: 'Finalidade da Biblioteca',
          description: 'Centraliza todas as atividades litúrgicas habituais (ex.: Louvor Congregacional, Oração Pastoral, Ofertório, Sermão, Avisos) com sua duração recomendada de referência.'
        },
        {
          title: 'Reaproveitamento Ágil',
          description: 'Ao montar qualquer cerimonial, você pode selecionar essas atividades em lote ou individualmente com poucos toques, eliminando digitação redundante e prevenindo conflitos de tempo.',
          callout: {
            type: 'tip',
            text: 'Cadastre aqui os itens mais comuns da rotina da sua comunidade para acelerar o planejamento de todos os futuros cerimoniais.'
          }
        }
      ]
    },

    adicionar_lote: {
      id: 'adicionar_lote',
      badge: 'Inclusão Rápida',
      badgeType: 'success',
      title: 'Adicionar Atividades em Lote',
      subtitle: 'Como preencher um bloco rapidamente a partir da biblioteca de itens.',
      sections: [
        {
          title: 'Como Funciona a Gaveta',
          description: 'Abra a gaveta inferior ("+ Em Lote") no bloco desejado, busque ou marque múltiplos cartões da biblioteca simultaneamente com simples toques.'
        },
        {
          title: 'Cálculo Sequencial Automático',
          description: 'Ao confirmar a adição, todas as atividades marcadas são inseridas sequencialmente no bloco, e os horários de início e término de cada item são recalculados instantaneamente.',
          callout: {
            type: 'info',
            text: 'Utilize o filtro de busca da gaveta para localizar rapidamente itens específicos e selecione quantos desejar antes de confirmar.'
          }
        }
      ]
    },

    adicionar_atividade: {
      id: 'adicionar_atividade',
      badge: 'Item Individual',
      badgeType: 'info',
      title: 'Adicionar Atividade Única ou Avulsa',
      subtitle: 'Cadastro pontual de uma atividade específica com duração e responsável.',
      sections: [
        {
          title: 'Linha de Adição Rápida (Quick Add)',
          description: 'Digite o título da atividade no bloco desejado, escolha a duração através dos chips táteis pré-definidos (ex.: 5m, 15m, 30m ou digite um tempo customizado) e pressione Enter.',
          callout: {
            type: 'tip',
            text: 'Enquanto você digita o nome, o sistema exibe sugestões preditivas inteligentes baseadas na biblioteca e no histórico do próprio evento.'
          }
        },
        {
          title: 'Atribuição Opcional de Responsável',
          description: 'Você pode indicar imediatamente quem executará a atividade clicando em "+ Responsável" ou complementar essa informação posteriormente.'
        }
      ]
    },

    momento_vs_atividade: {
      id: 'momento_vs_atividade',
      badge: 'Conceito Central',
      badgeType: 'primary',
      title: 'O que é Bloco e o que é Atividade?',
      subtitle: 'Entenda como o cronograma é estruturado em blocos temáticos e tarefas específicas.',
      sections: [
        {
          title: 'O que é um Bloco?',
          description: 'É a etapa macro ou bloco temático do culto/cerimonial, reunindo um grupo de ações com um propósito comum (ex.: Abertura, Louvor & Adoração, Ministração da Palavra, Encerramento). Cada bloco possui cor temática e tempo acumulado próprio.'
        },
        {
          title: 'O que é uma Atividade?',
          description: 'É a ação ou tarefa individual executada dentro de um bloco (ex.: Oração Inicial, Hino Congregacional, Leitura Bíblica, Mensagem Pastoral). Possui duração específica e pode ter um responsável designado.'
        }
      ],
      practicalExample: {
        title: 'Exemplo Prático na Programação',
        content: 'No Bloco "Louvor" (20 min no total), temos as Atividades: Cântico 1 (5 min), Cântico 2 (5 min), Cântico 3 (6 min) e Oração de Dedicação (4 min).'
      }
    },

    mensagem_telao: {
      id: 'mensagem_telao',
      badge: 'Tempo Real',
      badgeType: 'warning',
      title: 'Recados de Palco e Projeção ao Telão',
      subtitle: 'Comunicação instantânea e silenciosa da equipe técnica com o orador.',
      sections: [
        {
          title: 'Transmissão Silenciosa',
          description: 'Envie avisos diretamente da cabine de som ou cerimônia para o monitor de retorno no púlpito ou para o telão, sem interromper a fala de quem está ministrando.'
        },
        {
          title: 'Níveis de Urgência',
          description: 'Destaque a criticidade da mensagem selecionando entre:',
          bulletPoints: [
            'Informativo (Azul/Neutro): avisos de rotina ou sincronização de roteiro.',
            'Atenção (Amarelo): alertas de tempo limite ou ajustes de microfone.',
            'Urgente (Vermelho): situações prioritárias que requerem ação imediata.'
          ]
        },
        {
          title: 'Tempo de Exibição Contida',
          description: 'Configure a duração de exibição do comunicado na tela (15s, 30s, 1m ou fixo até dispensa manual).',
          callout: {
            type: 'tip',
            text: 'Use os atalhos rápidos pré-configurados para disparar comunicados frequentes como "Concluir em 2m" ou "Aproximar microfone" com agilidade máxima.'
          }
        }
      ]
    },

    painel_controle_guia: {
      id: 'painel_controle_guia',
      badge: 'Operação em Tempo Real',
      badgeType: 'primary',
      title: 'Guia Operacional do Painel de Controle',
      subtitle: 'Como gerenciar o tempo, cronômetro regressivo e avanço do culto em execução.',
      sections: [
        {
          title: 'Cronômetro Litúrgico Sincronizado',
          description: 'Acompanhe a contagem regressiva em tempo real sincronizada via WebSocket com todos os monitores de palco, telas públicas e navegadores conectados.'
        },
        {
          title: 'Ajustes Dinâmicos (+1m / -1m)',
          description: 'Adicione ou subtraia tempo rapidamente com um simples toque caso uma oração ou pregação precise de ajuste dinâmico, sem interrupção do cronômetro.'
        },
        {
          title: 'Sinalização Automática de Overtime',
          description: 'Se uma atividade estourar o tempo previsto, o contador entra automaticamente em modo Overtime (contagem progressiva em vermelho), evidenciando o atraso acumulado para a equipe.'
        },
        {
          title: 'Avanço e Transmissão de Atividades',
          description: 'Ao encerrar uma atividade, basta tocar na próxima no cronograma para transferir o foco do timer instantaneamente para ela.',
          callout: {
            type: 'info',
            text: 'Em smartphones e tablets, utilize as abas "Cronograma" e "Recados ao Palco" para alternar confortavelmente entre o acompanhamento do culto e o envio de alertas.'
          }
        }
      ]
    }
  };

  /**
   * Abre o modal contextual com o tópico correspondente.
   * Se o tópico não existir no catálogo, a operação é ignorada com segurança.
   */
  open(topicId: HelpTopicId): void {
    const topic = this.topicsCatalog[topicId];
    if (topic) {
      this.activeTopicState.set(topic);
    }
  }

  /**
   * Fecha o modal contextual ativo.
   */
  close(): void {
    this.activeTopicState.set(null);
  }

  /**
   * Obtém os dados de um tópico específico do catálogo sem alterar o estado.
   */
  getTopic(topicId: HelpTopicId): HelpTopic | undefined {
    return this.topicsCatalog[topicId];
  }
}
